import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { WifiOff } from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateNotificationsTree } from '../../config/reactQuery';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const online = useOnlineStatus();

  const queryClient = useQueryClient();

  // Centralize notification-driven cache invalidation.
  // We rely on Socket.IO for "new-notification" events (no polling loops here).
  useEffect(() => {
    const handleNotification = () => {
      invalidateNotificationsTree(queryClient);
    };

    window.addEventListener('new-notification', handleNotification);
    window.addEventListener('notification-read', handleNotification);

    return () => {
      window.removeEventListener('new-notification', handleNotification);
      window.removeEventListener('notification-read', handleNotification);
    };
  }, [queryClient]);

  return (
    <div className="min-h-screen bg-gray-50">
      {!online && (
        <div
          className="sticky top-0 z-[60] border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-center text-sm text-amber-950 shadow-sm"
          role="status"
          aria-live="polite"
        >
          <span className="inline-flex items-center justify-center gap-2">
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden />
            You are offline. Data may be stale and actions may fail until you reconnect.
          </span>
        </div>
      )}
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <div
        className={`min-h-screen transition-[padding] duration-300 ease-in-out ${
          sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
        }`}
      >
        {/* Header */}
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        {/* Page Content */}
        <main className="min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-4.5rem)] xl:min-h-[calc(100vh-5rem)] overflow-auto bg-gray-50">
          <div className="min-h-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
